import {
  FeedFollowBehavior,
  ImageFile,
  isAdminLayout,
  isOrgLayout,
  OrderDirection,
  ORG_TAGS_SIZE_LIMIT,
  PaginationInfo,
  RestrictDuplicateUrlOfCardsInTimeFrame,
} from '../shared';
import { ChannelAppTabSetting, ChannelAppTabUpdateInput, ChannelPreview } from './channelsNew';
import { graphqlApiWithPersisted } from './graphqlApi';
import { organisationQuery } from './helpers';
import { StoryCardCategory } from './storyCard';
import { User, UserGender, UserInvitedBy, UserPreview, UserRole, UserStatus } from './user';

export enum OrganisationTagType {
  EXTERNAL = 'EXTERNAL',
  INTERNAL = 'INTERNAL',
  EXTERNAL_STATUS = 'EXTERNAL_STATUS',
  EVENT = 'EVENT',
  USER = 'USER',
}

export enum OrganisationUsersFiltersSection {
  DEPARTMENT = 'DEPARTMENT',
  POSITION = 'POSITION',
  SUBSCRIPTION = 'SUBSCRIPTION',
}

export enum OrganisationUserOrderBy {
  EMAIL = 'EMAIL',
  SCREEN_NAME = 'SCREEN_NAME',
  AUTHORIZED_TIME = 'AUTHORIZED_TIME',
  REGISTERED = 'REGISTERED',
}

export enum OrganisationUserManagementStatus {
  OK = 'OK',
  SKIP = 'SKIP',
  ACCESS = 'ACCESS',
}

export enum OrganisationUserManagementChannelStatus {
  OK = 'OK',
  SKIP = 'SKIP',
}

export enum SessionExpirationTime {
  FOREVER = 'FOREVER',
  THREE_MONTHS = 'THREE_MONTHS',
  MONTH = 'MONTH',
  WEEK = 'WEEK',
  DAY = 'DAY',
}

export enum OrganisationTwoFactorForce {
  NONE = 'NONE',
  ALL = 'ALL',
  ALL_ADMINS = 'ALL_ADMINS',
  ORG_ADMINS = 'ORG_ADMINS',
  ALL_EDITORS = 'ALL_EDITORS',
}

export interface OrganisationUsersFilters {
  query?: string;
  genders?: UserGender[];
  filterDepartments?: string[];
  filterPositions?: string[];
  filterSubscriptions?: string[];
  organisationTagsId?: number[];
  usersId?: number[];
  rolesId?: number[];
  queryLocation?: string;
  channelsId?: [];
  excludeBanned?: boolean;
  onboardingMethods?: [UserInvitedBy | null];
  statuses?: UserStatus[];
}

export interface OrganisationUsersArgs {
  page?: number;
  size?: number;
  orderBy?: OrganisationUserOrderBy;
  orderDirection?: OrderDirection;
  filters?: OrganisationUsersFilters;
}

interface OrganisationUsersPayload {
  items: User[];
  pageInfo: PaginationInfo;
}

interface OrganisationUsersFiltersArgs {
  filters?: {
    sections?: OrganisationUsersFiltersSection[];
  };
}

interface OrganisationUsersResponse {
  data: {
    organisationUsers: OrganisationUsersPayload;
  };
}

interface OrganisationUserFilter {
  name: string;
  values: [{ count: number; value: string }];
}

interface OrganisationUsersFiltersResponse {
  data: {
    organisationUsersFilters: OrganisationUserFilter[];
  };
}

export interface OrganisationTagExternalSettings {
  feedFollowBehavior: FeedFollowBehavior;
}

export interface OrganisationTag {
  id: number;
  name: string;
  type: OrganisationTagType;
  settings?: OrganisationTagExternalSettings;
}

export interface OrganisationTagExternal extends OrganisationTag {
  feedFollowedStatus: boolean;
  subscriptionStatus: boolean;
  settings: OrganisationTagExternalSettings;
}

interface OrganisationPermissions {
  chatGroupAllowToCreate: boolean;
  chatP2PAllowToCreate: boolean;
  organisationInAppMessageAllowToCreate: boolean;
  organisationPushNotificationAllowToCreate: boolean;
  organisationTagAllowToManage: boolean;
}

interface OrganisationSettings {
  locationPlacesSearchEnabled: boolean;
}

export interface Organisation {
  id: number;
  uuid: string;
  name: string;
  subdomain: string;
  permissions: OrganisationPermissions;
  settings: OrganisationSettings;
  appLockEnabled: boolean;
  appTabsSettings: ChannelAppTabSetting;
  chatPromotionEnabled: boolean;
  defaultChannelUserRoleId: number;
  invitingIntoDefaultChannelsEnabled: boolean;
  locale: string;
  localeId: number;
  restrictDuplicateUrlOfCards: RestrictDuplicateUrlOfCardsInTimeFrame;
  sessionExpirationTime: SessionExpirationTime;
  sessionExpirationTimeApp: SessionExpirationTime;
  twoFactorForce: OrganisationTwoFactorForce;
  tags: OrganisationTag[]; //* artificial field
  categories: StoryCardCategory[]; //* artificial field
  role?: { id: number };
  pinnedStoryId?: number;
}

interface OrganisationResponse {
  data: {
    organisation: Organisation;
    storyCardCategories: { items: StoryCardCategory[] };
    internalTags?: { items: OrganisationTag[] };
    externalTags?: { items: OrganisationTag[] };
    externalStatusTags?: { items: OrganisationTag[] };
    userTags?: { items: OrganisationTag[] };
    eventTags?: { items: OrganisationTag[] };
  };
}

interface OrganisationRelatedStoriesArgs {
  channelId: number;
}

export interface OrganisationRelatedStory {
  id: number;
  image: ImageFile;
  subtitle: string;
  title: string;
}

interface OrganisationRelatedChannel {
  id: number;
  name: string;
  stories: OrganisationRelatedStory[];
  subdomain: string;
}

interface OrganisationRelatedStoriesResponse {
  data: {
    organisationRelatedStories: OrganisationRelatedChannel[];
  };
}

interface OrganisationDownloadAppLinkRequestForUserArgs {
  channelId: number;
  referralCode: string | null;
}

export interface OrganisationDownloadAppLinkRequestForUserPayload {
  link: string;
}

interface OrganisationDownloadAppLinkRequestForUserResponse {
  data: {
    organisationDownloadAppLinkRequestForUser: {
      payload: OrganisationDownloadAppLinkRequestForUserPayload;
    };
  };
}

export interface OrganisationTagsFiltersArgs {
  query?: string;
  types?: OrganisationTagType[];
}

export interface OrganisationTagsArgs {
  page?: number;
  size?: number;
  filters?: OrganisationTagsFiltersArgs;
}

interface OrganisationTagsData {
  items: OrganisationTag[];
  pageInfo: PaginationInfo;
}

interface OrganisationTagsResponse {
  data: {
    internalTags?: OrganisationTagsData;
    externalTags?: OrganisationTagsData;
    externalStatusTags?: OrganisationTagsData;
    userTags?: OrganisationTagsData;
    eventTags?: OrganisationTagsData;
  };
}

export interface OrganisationTagsExternalFiltersArgs {
  query?: string;
  feedFollowBehavior?: FeedFollowBehavior[];
  feedFollowedStatus?: boolean;
}

export interface OrganisationTagsExternalArgs {
  page?: number;
  size?: number;
  filters?: OrganisationTagsExternalFiltersArgs;
}

interface OrganisationTagsExternalData {
  items: OrganisationTagExternal[];
  pageInfo: PaginationInfo;
}

interface OrganisationTagsExternalResponse {
  data: {
    organisationTagsExternal: OrganisationTagsExternalData;
  };
}

export type OrganisationTagCounts = Record<OrganisationTagType, number>;

interface OrganisationTagCountsResponse {
  data: {
    internalTags?: { pageInfo: { totalItems: number } };
    externalTags?: { pageInfo: { totalItems: number } };
    externalStatusTags?: { pageInfo: { totalItems: number } };
    userTags?: { pageInfo: { totalItems: number } };
    eventTags?: { pageInfo: { totalItems: number } };
  };
}

interface OrganisationTagCreateArgs {
  name: string;
  type: OrganisationTagType;
}

interface OrganisationTagCreateData {
  payload: OrganisationTag;
  error: { __typename: string; message: string };
}

interface OrganisationTagCreateResponse {
  data: {
    organisationTagCreate: OrganisationTagCreateData;
  };
}

interface OrganisationTagUpdateArgs {
  tagId: number;
  name?: string;
}

interface OrganisationTagUpdateData {
  payload: OrganisationTag;
  error: { __typename: string; message: string };
}

interface OrganisationTagUpdateResponse {
  data: {
    organisationTagUpdate: OrganisationTagUpdateData;
  };
}

interface OrganisationTagExternalCreateArgs {
  name: string;
  feedFollowBehavior?: FeedFollowBehavior;
}

interface OrganisationTagExternalUpdateArgs {
  tagId: number;
  name?: string;
  feedFollowBehavior?: FeedFollowBehavior;
}

interface OrganisationTagExternalMutationData {
  payload: OrganisationTagExternal;
  error: { __typename: string; message: string };
}

interface OrganisationTagExternalCreateResponse {
  data: {
    organisationTagExternalCreate: OrganisationTagExternalMutationData;
  };
}

interface OrganisationTagExternalUpdateResponse {
  data: {
    organisationTagExternalUpdate: OrganisationTagExternalMutationData;
  };
}

interface OrganisationTagUpdateFollowedStatusArgs {
  tagId: number;
  status: boolean;
}

interface OrganisationTagUpdateFollowedStatusResponse {
  data: {
    organisationTagUpdateFollowedStatus: {
      payload: boolean;
    };
  };
}

interface OrganisationTagUpdateSubscriptionStatusArgs {
  tagId: number;
  status: boolean;
}

interface OrganisationTagUpdateSubscriptionStatusResponse {
  data: {
    organisationTagUpdateSubscriptionStatus: {
      payload: boolean;
    };
  };
}

interface OrganisationTagDeleteArgs {
  tagId: number;
}

interface OrganisationTagDeleteData {
  payload: boolean;
  error: { message: string };
}

interface OrganisationTagDeleteResponse {
  data: {
    organisationTagDelete: OrganisationTagDeleteData;
  };
}

interface OrganisationUserChannelManagementResult {
  channel: ChannelPreview;
  status: OrganisationUserManagementChannelStatus;
}

export interface OrganisationUserManagementResult {
  user: UserPreview;
  status: OrganisationUserManagementStatus;
  channels: OrganisationUserChannelManagementResult[];
}

export interface OrganisationAdminUpdateArgs {
  userIds: number[];
  role: UserRole;
}

interface OrganisationAdminUpdateResponse {
  data: {
    organisationAdminUpdate: { payload: OrganisationUserManagementResult[] };
  };
}

export interface OrganisationAdminRemoveArgs {
  userIds: number[];
}

interface OrganisationAdminRemoveResponse {
  data: {
    organisationAdminRemove: { payload: OrganisationUserManagementResult[] };
  };
}

export interface OrganisationUserRemoveFromChannelArgs {
  channelIds: number[];
  userIds: number[];
}

interface OrganisationUserRemoveFromChannelResponse {
  data: {
    organisationUserRemoveFromChannel: { payload: OrganisationUserManagementResult[] };
  };
}

export interface OrganisationChannelUserRemoveFromChannelArgs {
  channelId: number;
  userIds: number[];
}

interface OrganisationChannelUserRemoveFromChannelResponse {
  data: {
    organisationChannelUserRemoveFromChannel: { payload: OrganisationUserManagementResult[] };
  };
}

export interface OrganisationUserUpdateChannelRoleArgs {
  channelIds: number[];
  userIds: number[];
  role: UserRole;
}

interface OrganisationUserUpdateChannelRoleResponse {
  data: {
    organisationUserUpdateChannelRole: { payload: OrganisationUserManagementResult[] };
  };
}

interface OrganisationChannelUserUpdateChannelRoleArgs {
  channelId: number;
  userIds: number[];
  role: UserRole;
}

interface OrganisationChannelUserUpdateChannelRoleResponse {
  data: {
    organisationChannelUserUpdateChannelRole: { payload: OrganisationUserManagementResult[] };
  };
}

export interface OrganisationUserInviteByUserIdArgs {
  channelIds: number[];
  userIds: number[];
  role: UserRole;
  notifyExistingUsers: boolean;
}

interface OrganisationUserInviteByUserIdResponse {
  data: {
    organisationUserInviteByUserId: { payload: OrganisationUserManagementResult[] };
  };
}

interface OrganisationChannelUserInviteByUserIdArgs {
  channelId: number;
  userIds: number[];
  role: UserRole;
  notifyExistingUsers: boolean;
}

interface OrganisationChannelUserInviteByUserIdResponse {
  data: {
    organisationChannelUserInviteByUserId: { payload: OrganisationUserManagementResult[] };
  };
}

interface OrganisationUserInviteUserByEmailInput {
  email: string;
  screenName: string;
  department: string;
  position: string;
}

interface OrganisationUserInviteByEmailArgs {
  channelIds: number[];
  users: OrganisationUserInviteUserByEmailInput[];
  role: UserRole;
  notifyExistingUsers: boolean;
}

interface OrganisationUserInviteByEmailResponse {
  data: {
    organisationUserInviteByEmail: { payload: OrganisationUserManagementResult[] };
  };
}

interface OrganisationChannelUserInviteByEmailArgs {
  channelId: number;
  users: OrganisationUserInviteUserByEmailInput[];
  role: UserRole;
  notifyExistingUsers: boolean;
}

interface OrganisationChannelUserInviteByEmailResponse {
  data: {
    organisationChannelUserInviteByEmail: { payload: OrganisationUserManagementResult[] };
  };
}

interface OrganisationUserInviteByEmailWithGeneratePasswordArgs {
  channelIds: number[];
  users: OrganisationUserInviteUserByEmailInput[];
  role: UserRole;
  notifyExistingUsers: boolean;
}

interface OrganisationUserInviteByEmailWithGeneratePasswordResponse {
  data: {
    organisationUserInviteByEmailWithGeneratePassword: {
      payload: OrganisationUserManagementResult[];
    };
  };
}

interface OrganisationChannelUserInviteByEmailWithGeneratePasswordArgs {
  channelId: number;
  users: OrganisationUserInviteUserByEmailInput[];
  role: UserRole;
  notifyExistingUsers: boolean;
}

interface OrganisationChannelUserInviteByEmailWithGeneratePasswordResponse {
  data: {
    organisationChannelUserInviteByEmailWithGeneratePassword: {
      payload: OrganisationUserManagementResult[];
    };
  };
}

interface OrganisationUserInviteUserByCompanyIdInput {
  companyId: string;
  screenName: string;
  department: string;
  position: string;
}

interface OrganisationUserInviteByCompanyIdArgs {
  channelIds: number[];
  users: OrganisationUserInviteUserByCompanyIdInput[];
  role: UserRole;
}

interface OrganisationUserInviteByCompanyIdResponse {
  data: {
    organisationUserInviteByCompanyId: { payload: OrganisationUserManagementResult[] };
  };
}

interface OrganisationChannelUserInviteByCompanyId {
  channelId: number;
  users: OrganisationUserInviteUserByCompanyIdInput[];
  role: UserRole;
}

interface OrganisationChannelUserInviteByCompanyIdResponse {
  data: {
    organisationChannelUserInviteByCompanyId: { payload: OrganisationUserManagementResult[] };
  };
}

export interface OrganisationUserBanByUserIdArgs {
  userIds: number[];
}

interface OrganisationUserBanByUserIdResponse {
  data: {
    organisationUserBanByUserId: { payload: OrganisationUserManagementResult[] };
  };
}

export interface OrganisationUserUnbanByUserIdArgs {
  userIds: number[];
}

interface OrganisationUserUnbanByUserIdResponse {
  data: {
    organisationUserUnbanByUserId: { payload: OrganisationUserManagementResult[] };
  };
}

interface OrganisationUserRegeneratePasswordArgs {
  userIds: number[];
}

interface OrganisationUserRegeneratePasswordResponse {
  data: {
    organisationUserRegeneratePassword: { payload: OrganisationUserManagementResult[] };
  };
}

interface OrganisationUserRemindInvitationArgs {
  userIds: number[];
}

interface OrganisationUserRemindInvitationResponse {
  data: {
    organisationUserRemindInvitation: { payload: OrganisationUserManagementResult[] };
  };
}

interface OrganisationMemberRemoveArgs {
  userIds: number[];
}

interface OrganisationMemberRemoveResponse {
  data: {
    organisationMemberRemove: { payload: OrganisationUserManagementResult[] };
  };
}

interface OrganisationUserGenerateReportResponse {
  data: {
    organisationUserGenerateReport: { payload: boolean };
  };
}

interface OrganisationDeleteResponse {
  data: {
    organisationDelete: { payload: boolean };
  };
}

interface OrganisationUpdateArgs {
  fields: {
    name?: string;
    subdomain?: string;
    localeId?: number;
    localeIdDeepUpdate?: boolean;
    chatPromotionEnabled?: boolean;
    restrictDuplicateUrlOfCards?: RestrictDuplicateUrlOfCardsInTimeFrame;
    restrictDuplicateUrlOfCardsDeepUpdate?: boolean;
    twoFactorForce?: OrganisationTwoFactorForce;
    sessionExpirationTime?: SessionExpirationTime;
    sessionExpirationTimeApp?: SessionExpirationTime;
    appLockEnabled?: boolean;
    defaultChannelIds?: number[];
    defaultChannelUserRoleId?: number;
    invitingIntoDefaultChannelsEnabled?: boolean;
    appTabs?: ChannelAppTabUpdateInput[];
    appTabsDeepUpdate?: boolean;
  };
}

interface OrganisationUpdateResponse {
  data: {
    organisationUpdate: { payload: Organisation };
  };
}

export const organisationApi = graphqlApiWithPersisted
  .enhanceEndpoints({ addTagTypes: ['FollowableTags'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      organisation: builder.query<Organisation, void>({
        query: () => ({
          url: '/graphql/webapp?organisation',
          method: 'POST',
          body: {
            query: `query Organisation($size: Int) {
              organisation {
                ${organisationQuery}
              }

              storyCardCategories {
                items {
                  id
                  name
                }
              }

              internalTags: organisationTags(size: $size, filters: { types: [INTERNAL] })
              @include(if: ${isAdminLayout() || isOrgLayout()}) {
                items { id name type }
              }

              externalTags: organisationTagsExternal(size: $size) {
                items { id name type settings { feedFollowBehavior } }
              }

              externalStatusTags: organisationTags(size: $size, filters: { types: [EXTERNAL_STATUS] }) {
                items { id name type }
              }

              userTags: organisationTags(size: $size, filters: { types: [USER] }) {
                items { id name type }
              }

              eventTags: organisationTags(size: $size, filters: { types: [EVENT] }) {
                items { id name type }
              }
            }`,
            variables: { size: ORG_TAGS_SIZE_LIMIT },
          },
        }),
        forceRefetch: () => true,
        transformResponse: (response: OrganisationResponse) => {
          return {
            ...response.data.organisation,
            categories: response.data.storyCardCategories.items,
            tags: [
              ...(response.data.internalTags?.items ?? []),
              ...(response.data.externalTags?.items ?? []),
              ...(response.data.externalStatusTags?.items ?? []),
              ...(response.data.userTags?.items ?? []),
              ...(response.data.eventTags?.items ?? []),
            ],
          };
        },
      }),
      organisationUsers: builder.query<OrganisationUsersPayload, OrganisationUsersArgs>({
        query: ({
          page = 1,
          size = 15,
          orderBy = OrganisationUserOrderBy.SCREEN_NAME,
          orderDirection = OrderDirection.ASC,
          filters,
        }) => ({
          url: '/graphql/webapp?organisationUsers',
          method: 'POST',
          body: {
            query: `query OrganisationUsers($page: Int, $size: Int, $orderBy: OrganisationUserOrderByFieldsEnum!, $orderDirection: OrderDirectionEnum!, $filters: GetOrganisationUsersFiltersArgsType) {
              organisationUsers(page: $page, size: $size, orderBy: $orderBy, orderDirection: $orderDirection, filters: $filters) {
                items {
                  id
                  avatar {
                    averageColor
                    externalId
                    externalType
                    id
                    originalFilename
                    rightholder
                    status
                    statusCopyrightId
                    thumb
                    url
                  }
                  department
                  email
                  phone
                  position
                  screenName
                  roleId
                  gender
                  location {
                    text
                  }
                  ${
                    isOrgLayout()
                      ? `
                      accessedChannels {
                        id, name, role, subdomain
                      }
                      authorizedAt
                      registeredAt
                      isBanned
                      organisationTags {
                        id
                        name
                      }
                      statistic {
                        comments
                        likesOnComments
                        posts
                        reactions
                        shares
                      }
                      companyId
                      subscription
                      referralUsersCount
                      referredByUser {
                        screenName
                      }
                      onboardingMethod`
                      : ''
                  }
                }
                pageInfo {
                  page
                  perPage
                  hasNextPage
                  hasPreviousPage
                  totalItems
                  totalPages
                }
              }
            }`,
            variables: { page, size, orderBy, orderDirection, filters },
          },
        }),
        forceRefetch: () => true,
        transformResponse: (response: OrganisationUsersResponse) => response.data.organisationUsers,
      }),
      organisationUsersFilters: builder.query<
        OrganisationUserFilter[],
        OrganisationUsersFiltersArgs
      >({
        query: ({ filters }) => ({
          url: '/graphql/webapp?organisationUsersFilters',
          method: 'POST',
          body: {
            query: `query OrganisationUsersFilters($filters: GetOrganisationUsersFiltersArgs) {
              organisationUsersFilters(filters: $filters) {
                name
                values {
                  count
                  value
                }
              }
            }`,
            variables: { filters },
          },
        }),
        forceRefetch: () => true,
        transformResponse: (response: OrganisationUsersFiltersResponse) =>
          response.data.organisationUsersFilters,
      }),
      organisationRelatedStories: builder.query<
        OrganisationRelatedChannel[],
        OrganisationRelatedStoriesArgs
      >({
        query: ({ channelId }) => ({
          url: '/graphql/webapp?organisationRelatedStories',
          method: 'POST',
          body: {
            query: `query OrganisationRelatedStories($channelId: Int!) {
              organisationRelatedStories(channelId: $channelId) {
                id
                name
                stories {
                  id
                  title
                  subtitle
                  image {
                    id
                    url
                    rightholder
                  }
                }
              }
            }`,
            variables: { channelId },
          },
        }),
        forceRefetch: () => true,
        transformResponse: (response: OrganisationRelatedStoriesResponse) =>
          response.data.organisationRelatedStories,
      }),
      organisationDownloadAppLinkRequestForUser: builder.mutation<
        OrganisationDownloadAppLinkRequestForUserPayload,
        OrganisationDownloadAppLinkRequestForUserArgs
      >({
        query: ({ channelId, referralCode }) => ({
          url: '/graphql/webapp?organisationDownloadAppLinkRequestForUser',
          method: 'POST',
          body: {
            query: `mutation OrganisationDownloadAppLinkRequestForUser($input: GetOrganisationDownloadAppLinkArgsType!) {
              organisationDownloadAppLinkRequestForUser(input: $input) {
                payload {
                  link
                }
                error {
                  ... on OrganisationDoesNotHaveLinkToDownloadAppError {
                    message
                  }
                  ... on UnsupportedDevicePlatformError {
                    message
                  }
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: { input: { channelId, referralCode } },
          },
        }),
        transformResponse: (response: OrganisationDownloadAppLinkRequestForUserResponse) =>
          response.data.organisationDownloadAppLinkRequestForUser.payload,
      }),
      organisationTags: builder.query<OrganisationTagsData, OrganisationTagsArgs>({
        query: ({ page = 1, size = 10, filters }) => ({
          url: '/graphql/webapp?organisationTags',
          method: 'POST',
          body: {
            query: `query OrganisationTags($page: Int, $size: Int, $query: String) {
              internalTags: organisationTags(page: $page, size: $size, filters: { types: [INTERNAL], query: $query })
              @include(if: ${filters?.types?.includes(OrganisationTagType.INTERNAL)}) {
                items { id name type }
                pageInfo { page perPage totalItems totalPages hasNextPage hasPreviousPage }
              }

              externalTags: organisationTagsExternal(page: $page, size: $size, filters: { query: $query })
              @include(if: ${filters?.types?.includes(OrganisationTagType.EXTERNAL)}) {
                items { id name type settings { feedFollowBehavior } }
                pageInfo { page perPage totalItems totalPages hasNextPage hasPreviousPage }
              }

              externalStatusTags: organisationTags(page: $page, size: $size, filters: { types: [EXTERNAL_STATUS], query: $query })
              @include(if: ${filters?.types?.includes(OrganisationTagType.EXTERNAL_STATUS)}) {
                items { id name type }
                pageInfo { page perPage totalItems totalPages hasNextPage hasPreviousPage }
              }

              userTags: organisationTags(page: $page, size: $size, filters: { types: [USER], query: $query })
              @include(if: ${filters?.types?.includes(OrganisationTagType.USER)}) {
                items { id name type }
                pageInfo { page perPage totalItems totalPages hasNextPage hasPreviousPage }
              }

              eventTags: organisationTags(page: $page, size: $size, filters: { types: [EVENT], query: $query })
              @include(if: ${filters?.types?.includes(OrganisationTagType.EVENT)}) {
                items { id name type }
                pageInfo { page perPage totalItems totalPages hasNextPage hasPreviousPage }
              }
            }`,
            variables: { page, size, query: filters?.query ?? '' },
          },
        }),
        forceRefetch: () => true,
        transformResponse: (response: OrganisationTagsResponse) => {
          return {
            items: [
              ...(response.data.internalTags?.items ?? []),
              ...(response.data.externalTags?.items ?? []),
              ...(response.data.externalStatusTags?.items ?? []),
              ...(response.data.userTags?.items ?? []),
              ...(response.data.eventTags?.items ?? []),
            ],
            pageInfo:
              response.data.internalTags?.pageInfo ??
              response.data.externalTags?.pageInfo ??
              response.data.externalStatusTags?.pageInfo ??
              response.data.userTags?.pageInfo ??
              response.data.eventTags?.pageInfo ??
              ({
                page: 1,
                perPage: 0,
                totalItems: 0,
                totalPages: 0,
                hasNextPage: false,
                hasPreviousPage: false,
              } as PaginationInfo),
          };
        },
      }),
      organisationTagsExternal: builder.query<
        OrganisationTagsExternalData,
        OrganisationTagsExternalArgs
      >({
        query: ({ page = 1, size = 10, filters }) => ({
          url: '/graphql/webapp?organisationTagsExternal',
          method: 'POST',
          body: {
            query: `query OrganisationTagsExternal($page: Int, $size: Int, $filters: GetOrganisationTagsExternalFiltersArgs) {
              organisationTagsExternal(page: $page, size: $size, filters: $filters) {
                items {
                  id
                  name
                  type
                  feedFollowedStatus
                  subscriptionStatus
                  settings {
                    feedFollowBehavior
                  }
                }
                pageInfo { page perPage totalItems totalPages hasNextPage hasPreviousPage }
              }
            }`,
            variables: { page, size, filters },
          },
        }),
        forceRefetch: () => true,
        providesTags: (_, __, { filters }) =>
          filters?.feedFollowedStatus === undefined ? [] : ['FollowableTags'],
        transformResponse: (response: OrganisationTagsExternalResponse) =>
          response.data.organisationTagsExternal,
      }),
      organisationTagCounts: builder.query<OrganisationTagCounts, void>({
        query: () => ({
          url: '/graphql/webapp?organisationTagCounts',
          method: 'POST',
          body: {
            query: `query OrganisationTagCounts {
              internalTags: organisationTags(size: 1, filters: { types: [INTERNAL] }) {
                pageInfo { totalItems }
              }

              externalTags: organisationTagsExternal(size: 1) {
                pageInfo { totalItems }
              }

              externalStatusTags: organisationTags(size: 1, filters: { types: [EXTERNAL_STATUS] }) {
                pageInfo { totalItems }
              }

              userTags: organisationTags(size: 1, filters: { types: [USER] }) {
                pageInfo { totalItems }
              }

              eventTags: organisationTags(size: 1, filters: { types: [EVENT] }) {
                pageInfo { totalItems }
              }
            }`,
          },
        }),
        forceRefetch: () => true,
        transformResponse: ({
          data: { internalTags, externalTags, externalStatusTags, userTags, eventTags },
        }: OrganisationTagCountsResponse) => ({
          [OrganisationTagType.INTERNAL]: internalTags?.pageInfo.totalItems ?? 0,
          [OrganisationTagType.EXTERNAL]: externalTags?.pageInfo.totalItems ?? 0,
          [OrganisationTagType.EXTERNAL_STATUS]: externalStatusTags?.pageInfo.totalItems ?? 0,
          [OrganisationTagType.USER]: userTags?.pageInfo.totalItems ?? 0,
          [OrganisationTagType.EVENT]: eventTags?.pageInfo.totalItems ?? 0,
        }),
      }),
      organisationTagUpdateFollowedStatus: builder.mutation<
        boolean,
        OrganisationTagUpdateFollowedStatusArgs
      >({
        query: ({ tagId, status }) => ({
          url: '/graphql/webapp?organisationTagUpdateFollowedStatus',
          method: 'POST',
          body: {
            query: `mutation OrganisationTagUpdateFollowedStatus($input: OrganisationUpdateFollowedStatusInput!) {
              organisationTagUpdateFollowedStatus(input: $input) {
                payload
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                  ... on OrganisationTagNotFoundError {
                    message
                  }
                }
              }
            }`,
            variables: { input: { tagId, status } },
          },
        }),
        invalidatesTags: ['FollowableTags'],
        transformResponse: (response: OrganisationTagUpdateFollowedStatusResponse) =>
          response.data.organisationTagUpdateFollowedStatus.payload,
      }),
      organisationTagUpdateSubscriptionStatus: builder.mutation<
        boolean,
        OrganisationTagUpdateSubscriptionStatusArgs
      >({
        query: ({ tagId, status }) => ({
          url: '/graphql/webapp?organisationTagUpdateSubscriptionStatus',
          method: 'POST',
          body: {
            query: `mutation OrganisationTagUpdateSubscriptionStatus($input: OrganisationUpdateSubscriptionStatusInput!) {
              organisationTagUpdateSubscriptionStatus(input: $input) {
                payload
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                  ... on OrganisationTagNotFoundError {
                    message
                  }
                }
              }
            }`,
            variables: { input: { tagId, status } },
          },
        }),
        transformResponse: (response: OrganisationTagUpdateSubscriptionStatusResponse) =>
          response.data.organisationTagUpdateSubscriptionStatus.payload,
      }),
      organisationTagCreate: builder.mutation<OrganisationTagCreateData, OrganisationTagCreateArgs>(
        {
          query: ({ name, type }) => ({
            url: '/graphql/webapp?organisationTagCreate',
            method: 'POST',
            body: {
              query: `mutation OrganisationTagCreate($input: OrganisationTagCreateInput!) {
                organisationTagCreate(input: $input) {
                  payload {
                    id
                    name
                    type
                  }
                  error {
                    __typename
                    ... on UnknownError {
                      message
                      kind
                    }
                    ... on OrganisationTagConflictNameError {
                      message
                    }
                    ... on OrganisationTagsReachedLimitError {
                      message
                    }
                  }
                }
              }`,
              variables: {
                input: { fields: { name, type } },
              },
            },
          }),
          transformResponse: (response: OrganisationTagCreateResponse) =>
            response.data.organisationTagCreate,
        }
      ),
      organisationTagUpdate: builder.mutation<OrganisationTagUpdateData, OrganisationTagUpdateArgs>(
        {
          query: ({ tagId, name }) => ({
            url: '/graphql/webapp?organisationTagUpdate',
            method: 'POST',
            body: {
              query: `mutation OrganisationTagUpdate($input: OrganisationTagUpdateInput!) {
                organisationTagUpdate(input: $input) {
                  payload {
                    id
                    name
                    type
                  }
                  error {
                    __typename
                    ... on UnknownError {
                      message
                      kind
                    }
                    ... on OrganisationTagConflictNameError {
                      message
                    }
                    ... on OrganisationTagNotFoundError {
                      message
                    }
                  }
                }
              }`,
              variables: {
                input: {
                  tagId,
                  fields: { name },
                },
              },
            },
          }),
          transformResponse: (response: OrganisationTagUpdateResponse) =>
            response.data?.organisationTagUpdate,
        }
      ),
      organisationTagExternalCreate: builder.mutation<
        OrganisationTagExternalMutationData,
        OrganisationTagExternalCreateArgs
      >({
        query: ({ name, feedFollowBehavior }) => ({
          url: '/graphql/webapp?organisationTagExternalCreate',
          method: 'POST',
          body: {
            query: `mutation OrganisationTagExternalCreate($input: OrganisationTagExternalCreateInput!) {
              organisationTagExternalCreate(input: $input) {
                payload {
                  id
                  name
                  type
                  feedFollowedStatus
                  subscriptionStatus
                  settings {
                    feedFollowBehavior
                  }
                }
                error {
                  __typename
                  ... on UnknownError {
                    message
                    kind
                  }
                  ... on OrganisationTagConflictNameError {
                    message
                  }
                  ... on OrganisationTagsReachedLimitError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: {
                fields: { name, ...(feedFollowBehavior && { settings: { feedFollowBehavior } }) },
              },
            },
          },
        }),
        transformResponse: (response: OrganisationTagExternalCreateResponse) =>
          response.data.organisationTagExternalCreate,
      }),
      organisationTagExternalUpdate: builder.mutation<
        OrganisationTagExternalMutationData,
        OrganisationTagExternalUpdateArgs
      >({
        query: ({ tagId, name, feedFollowBehavior }) => ({
          url: '/graphql/webapp?organisationTagExternalUpdate',
          method: 'POST',
          body: {
            query: `mutation OrganisationTagExternalUpdate($input: OrganisationTagExternalUpdateInput!) {
              organisationTagExternalUpdate(input: $input) {
                payload {
                  id
                  name
                  type
                  feedFollowedStatus
                  subscriptionStatus
                  settings {
                    feedFollowBehavior
                  }
                }
                error {
                  __typename
                  ... on UnknownError {
                    message
                    kind
                  }
                  ... on OrganisationTagConflictNameError {
                    message
                  }
                  ... on OrganisationTagNotFoundError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: {
                tagId,
                fields: { name, ...(feedFollowBehavior && { settings: { feedFollowBehavior } }) },
              },
            },
          },
        }),
        transformResponse: (response: OrganisationTagExternalUpdateResponse) =>
          response.data.organisationTagExternalUpdate,
      }),
      organisationTagDelete: builder.mutation<boolean, OrganisationTagDeleteArgs>({
        query: ({ tagId }) => ({
          url: '/graphql/webapp?organisationTagDelete',
          method: 'POST',
          body: {
            query: `mutation OrganisationTagDelete($input: OrganisationTagDeleteInput!) {
              organisationTagDelete(input: $input) {
                payload
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                  ... on OrganisationTagNotFoundError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: { tagId },
            },
          },
        }),
        transformResponse: (response: OrganisationTagDeleteResponse) =>
          response.data.organisationTagDelete.payload,
      }),
      organisationAdminUpdate: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationAdminUpdateArgs
      >({
        query: ({ userIds, role }) => ({
          url: '/graphql/webapp?organisationAdminUpdate',
          method: 'POST',
          body: {
            query: `mutation OrganisationAdminUpdate($input: OrganisationAdminUpdateInput!) {
              organisationAdminUpdate(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { userIds, role } },
          },
        }),
        transformResponse: (response: OrganisationAdminUpdateResponse) =>
          response.data.organisationAdminUpdate.payload,
      }),
      organisationAdminRemove: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationAdminRemoveArgs
      >({
        query: ({ userIds }) => ({
          url: '/graphql/webapp?organisationAdminRemove',
          method: 'POST',
          body: {
            query: `mutation OrganisationAdminRemove($input: OrganisationAdminRemoveInput!) {
              organisationAdminRemove(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { userIds } },
          },
        }),
        transformResponse: (response: OrganisationAdminRemoveResponse) =>
          response.data.organisationAdminRemove.payload,
      }),
      organisationUserRemoveFromChannel: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationUserRemoveFromChannelArgs
      >({
        query: ({ channelIds, userIds }) => ({
          url: '/graphql/webapp?organisationUserRemoveFromChannel',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserRemoveFromChannel($input: OrganisationUserRemoveFromChannelInput!) {
              organisationUserRemoveFromChannel(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelIds, userIds } },
          },
        }),
        transformResponse: (response: OrganisationUserRemoveFromChannelResponse) =>
          response.data.organisationUserRemoveFromChannel.payload,
      }),
      organisationChannelUserRemoveFromChannel: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationChannelUserRemoveFromChannelArgs
      >({
        query: ({ channelId, userIds }) => ({
          url: '/graphql/webapp?organisationChannelUserRemoveFromChannel',
          method: 'POST',
          body: {
            query: `mutation OrganisationChannelUserRemoveFromChannel($input: OrganisationChannelUserRemoveFromChannelInput!) {
              organisationChannelUserRemoveFromChannel(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelId, userIds } },
          },
        }),
        transformResponse: (response: OrganisationChannelUserRemoveFromChannelResponse) =>
          response.data.organisationChannelUserRemoveFromChannel.payload,
      }),
      organisationUserUpdateChannelRole: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationUserUpdateChannelRoleArgs
      >({
        query: ({ channelIds, userIds, role }) => ({
          url: '/graphql/webapp?organisationUserUpdateChannelRole',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserUpdateChannelRole($input: OrganisationUserUpdateChannelRoleInput!) {
              organisationUserUpdateChannelRole(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelIds, userIds, role } },
          },
        }),
        transformResponse: (response: OrganisationUserUpdateChannelRoleResponse) =>
          response.data.organisationUserUpdateChannelRole.payload,
      }),
      organisationChannelUserUpdateChannelRole: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationChannelUserUpdateChannelRoleArgs
      >({
        query: ({ channelId, userIds, role }) => ({
          url: '/graphql/webapp?organisationChannelUserUpdateChannelRole',
          method: 'POST',
          body: {
            query: `mutation OrganisationChannelUserUpdateChannelRole($input: OrganisationChannelUserUpdateChannelRoleInput!) {
              organisationChannelUserUpdateChannelRole(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelId, userIds, role } },
          },
        }),
        transformResponse: (response: OrganisationChannelUserUpdateChannelRoleResponse) =>
          response.data.organisationChannelUserUpdateChannelRole.payload,
      }),
      organisationUserInviteByUserId: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationUserInviteByUserIdArgs
      >({
        query: ({ channelIds, userIds, role, notifyExistingUsers }) => ({
          url: '/graphql/webapp?organisationUserInviteByUserId',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserInviteByUserId($input: OrganisationUserInviteByUserIdInput!) {
              organisationUserInviteByUserId(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelIds, userIds, role, notifyExistingUsers } },
          },
        }),
        transformResponse: (response: OrganisationUserInviteByUserIdResponse) =>
          response.data.organisationUserInviteByUserId.payload,
      }),
      organisationChannelUserInviteByUserId: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationChannelUserInviteByUserIdArgs
      >({
        query: ({ channelId, userIds, role, notifyExistingUsers }) => ({
          url: '/graphql/webapp?organisationChannelUserInviteByUserId',
          method: 'POST',
          body: {
            query: `mutation OrganisationChannelUserInviteByUserId($input: OrganisationChannelUserInviteByUserIdInput!) {
              organisationChannelUserInviteByUserId(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelId, userIds, role, notifyExistingUsers } },
          },
        }),
        transformResponse: (response: OrganisationChannelUserInviteByUserIdResponse) =>
          response.data.organisationChannelUserInviteByUserId.payload,
      }),
      organisationUserInviteByEmail: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationUserInviteByEmailArgs
      >({
        query: ({ channelIds, users, role, notifyExistingUsers }) => ({
          url: '/graphql/webapp?organisationUserInviteByEmail',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserInviteByEmail($input: OrganisationUserInviteByEmailInput!) {
              organisationUserInviteByEmail(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelIds, users, role, notifyExistingUsers } },
          },
        }),
        transformResponse: (response: OrganisationUserInviteByEmailResponse) =>
          response.data.organisationUserInviteByEmail.payload,
      }),
      organisationChannelUserInviteByEmail: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationChannelUserInviteByEmailArgs
      >({
        query: ({ channelId, users, role, notifyExistingUsers }) => ({
          url: '/graphql/webapp?organisationChannelUserInviteByEmail',
          method: 'POST',
          body: {
            query: `mutation OrganisationChannelUserInviteByEmail($input: OrganisationChannelUserInviteByEmailInput!) {
              organisationChannelUserInviteByEmail(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelId, users, role, notifyExistingUsers } },
          },
        }),
        transformResponse: (response: OrganisationChannelUserInviteByEmailResponse) =>
          response.data.organisationChannelUserInviteByEmail.payload,
      }),
      organisationUserInviteByEmailWithGeneratePassword: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationUserInviteByEmailWithGeneratePasswordArgs
      >({
        query: ({ channelIds, users, role, notifyExistingUsers }) => ({
          url: '/graphql/webapp?organisationUserInviteByEmailWithGeneratePassword',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserInviteByEmailWithGeneratePassword($input: OrganisationUserInviteByEmailInput!) {
              organisationUserInviteByEmailWithGeneratePassword(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelIds, users, role, notifyExistingUsers } },
          },
        }),
        transformResponse: (response: OrganisationUserInviteByEmailWithGeneratePasswordResponse) =>
          response.data.organisationUserInviteByEmailWithGeneratePassword.payload,
      }),
      organisationChannelUserInviteByEmailWithGeneratePassword: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationChannelUserInviteByEmailWithGeneratePasswordArgs
      >({
        query: ({ channelId, users, role, notifyExistingUsers }) => ({
          url: '/graphql/webapp?organisationChannelUserInviteByEmailWithGeneratePassword',
          method: 'POST',
          body: {
            query: `mutation OrganisationChannelUserInviteByEmailWithGeneratePassword($input: OrganisationChannelUserInviteByEmailInput!) {
              organisationChannelUserInviteByEmailWithGeneratePassword(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelId, users, role, notifyExistingUsers } },
          },
        }),
        transformResponse: (
          response: OrganisationChannelUserInviteByEmailWithGeneratePasswordResponse
        ) => response.data.organisationChannelUserInviteByEmailWithGeneratePassword.payload,
      }),
      organisationUserInviteByCompanyId: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationUserInviteByCompanyIdArgs
      >({
        query: ({ channelIds, users, role }) => ({
          url: '/graphql/webapp?organisationUserInviteByCompanyId',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserInviteByCompanyId($input: OrganisationUserInviteByCompanyIdInput!) {
              organisationUserInviteByCompanyId(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelIds, users, role } },
          },
        }),
        transformResponse: (response: OrganisationUserInviteByCompanyIdResponse) =>
          response.data.organisationUserInviteByCompanyId.payload,
      }),
      organisationChannelUserInviteByCompanyId: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationChannelUserInviteByCompanyId
      >({
        query: ({ channelId, users, role }) => ({
          url: '/graphql/webapp?organisationChannelUserInviteByCompanyId',
          method: 'POST',
          body: {
            query: `mutation OrganisationChannelUserInviteByCompanyId($input: OrganisationChannelUserInviteByCompanyIdInput!) {
              organisationChannelUserInviteByCompanyId(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { channelId, users, role } },
          },
        }),
        transformResponse: (response: OrganisationChannelUserInviteByCompanyIdResponse) =>
          response.data.organisationChannelUserInviteByCompanyId.payload,
      }),
      organisationUserBanByUserId: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationUserBanByUserIdArgs
      >({
        query: ({ userIds }) => ({
          url: '/graphql/webapp?organisationUserBanByUserId',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserBanByUserId($input: OrganisationUserBanByUserIdInput!) {
              organisationUserBanByUserId(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { userIds } },
          },
        }),
        transformResponse: (response: OrganisationUserBanByUserIdResponse) =>
          response.data.organisationUserBanByUserId.payload,
      }),
      organisationUserUnbanByUserId: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationUserUnbanByUserIdArgs
      >({
        query: ({ userIds }) => ({
          url: '/graphql/webapp?organisationUserUnbanByUserId',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserUnbanByUserId($input: OrganisationUserUnbanByUserIdInput!) {
              organisationUserUnbanByUserId(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { userIds } },
          },
        }),
        transformResponse: (response: OrganisationUserUnbanByUserIdResponse) =>
          response.data.organisationUserUnbanByUserId.payload,
      }),
      organisationUserRegeneratePassword: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationUserRegeneratePasswordArgs
      >({
        query: ({ userIds }) => ({
          url: '/graphql/webapp?organisationUserRegeneratePassword',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserRegeneratePassword($input: OrganisationUserRegeneratePasswordInput!) {
              organisationUserRegeneratePassword(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { userIds } },
          },
        }),
        transformResponse: (response: OrganisationUserRegeneratePasswordResponse) =>
          response.data.organisationUserRegeneratePassword.payload,
      }),
      organisationUserRemindInvitation: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationUserRemindInvitationArgs
      >({
        query: ({ userIds }) => ({
          url: '/graphql/webapp?organisationUserRemindInvitation',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserRemindInvitation($input: OrganisationUserRemindInvitationInput!) {
              organisationUserRemindInvitation(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { userIds } },
          },
        }),
        transformResponse: (response: OrganisationUserRemindInvitationResponse) =>
          response.data.organisationUserRemindInvitation.payload,
      }),
      organisationMemberRemove: builder.mutation<
        OrganisationUserManagementResult[],
        OrganisationMemberRemoveArgs
      >({
        query: ({ userIds }) => ({
          url: '/graphql/webapp?organisationMemberRemove',
          method: 'POST',
          body: {
            query: `mutation OrganisationMemberRemove($input: OrganisationMemberRemoveInput!) {
              organisationMemberRemove(input: $input) {
                payload {
                  channels {
                    channel {
                      id
                      name
                      subdomain
                    }
                    status
                  }
                  status
                  user {
                    id
                    screenName
                    email
                    companyId
                    avatar {
                      url
                    }
                  }
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { userIds } },
          },
        }),
        transformResponse: (response: OrganisationMemberRemoveResponse) =>
          response.data.organisationMemberRemove.payload,
      }),
      organisationUserGenerateReport: builder.mutation<boolean, void>({
        query: () => ({
          url: '/graphql/webapp?organisationUserGenerateReport',
          method: 'POST',
          body: {
            query: `mutation OrganisationUserGenerateReport {
              organisationUserGenerateReport {
                payload
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
          },
        }),
        transformResponse: (response: OrganisationUserGenerateReportResponse) =>
          response.data.organisationUserGenerateReport.payload,
      }),
      organisationUpdate: builder.mutation<Organisation, OrganisationUpdateArgs>({
        query: ({ fields }) => ({
          url: '/graphql/webapp?organisationUpdate',
          method: 'POST',
          body: {
            query: `mutation OrganisationUpdate($input: OrganisationUpdateInput!) {
              organisationUpdate(input: $input) {
                payload {
                  ${organisationQuery}
                }
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
            variables: { input: { fields } },
          },
        }),
        transformResponse: (response: OrganisationUpdateResponse) =>
          response.data.organisationUpdate.payload,
      }),
      organisationDelete: builder.mutation<boolean, void>({
        query: () => ({
          url: '/graphql/webapp?organisationDelete',
          method: 'POST',
          body: {
            query: `mutation OrganisationDelete {
              organisationDelete {
                payload
                error {
                  ... on UnknownError {
                    message
                    kind
                  }
                }
              }
            }`,
          },
        }),
        transformResponse: (response: OrganisationDeleteResponse) =>
          response.data.organisationDelete.payload,
      }),
    }),
  });

export const {
  useOrganisationQuery,
  useOrganisationUsersQuery,
  useLazyOrganisationUsersQuery,
  useOrganisationUsersFiltersQuery,
  useOrganisationRelatedStoriesQuery,
  useOrganisationTagsQuery,
  useOrganisationTagsExternalQuery,
  useOrganisationTagCountsQuery,
  useOrganisationTagUpdateFollowedStatusMutation,
  useOrganisationTagUpdateSubscriptionStatusMutation,
  useOrganisationDownloadAppLinkRequestForUserMutation,
} = organisationApi;
